import {
  Container,
  FederatedPointerEvent,
  Graphics,
  PerspectiveMesh,
  Rectangle,
  RenderTexture,
  Sprite,
  Texture
} from 'pixi.js';
import { gsap } from 'gsap';
import { BookConstants } from '@/constants/bookConstants';
import { clientPointToElement } from '@/utils/readerViewportCoordinates';
import {
  calculateBookThickness,
  getHiddenThicknessSide,
  getMaxThicknessLineCount,
  getThicknessLineCount
} from '../display/bookThickness';
import { createDisplayLayout } from '../display/displayMode';
import {
  resolvePageCornerSide,
  resolvePageFaceCornerSide,
  resolveThicknessCornerGeometry,
  resolveThicknessLineGeometry
} from '../display/pageRadius';
import {
  resolvePageTransitionFrame,
  shouldRenderPageTransitionFrame
} from '../display/pageTransition';
import { shouldCommitFold } from '../display/foldCommitThreshold';
import {
  resolveHardCoverFrame,
  resolveHardCoverProgress,
  resolveFlipCalculationPath,
  resolveSingleHardCoverFrame,
  resolveHardCoverTransition,
  type HardCoverTransition
} from '../display/coverFlip';
import { FlipCalculator } from '../calc/FlipCalculator';
import { PixiPage } from '../page/PixiPage';
import { createShadowSprite } from '../display/shadows';
import {
  buildStaticLayerVisiblePolygon,
  isCloseBookStaticMaskState,
  shouldRetainStaticLayerMask
} from '../calc/staticLayerMask';
import { calculateFlipAnimationDuration } from './flipAnimationDuration';
import type {
  DisplayLayout,
  FlipCorner,
  FlipDirection,
  FlipSource,
  FlipPageIndexes,
  PageRect,
  Point,
  Segment,
  ShadowData,
  PixiBookRendererOptions,
  PageSpineSide
} from '@/types/book';

export class PixiBookRenderer {
  readonly options: PixiBookRendererOptions;
  readonly root = new Container();
  /** 内部居中容器：翻页跨页偏移修改此容器的 x 坐标，不影响用户对 root 的 transform 控制 */
  readonly centerContainer = new Container();
  readonly pages: PixiPage[] = [];

  private readonly backgroundLayer = new Container();
  private readonly leftBookDropShadowContainer = new Container();
  private readonly rightBookDropShadowContainer = new Container();
  private readonly leftBookDropShadow: Sprite;
  private readonly rightBookDropShadow: Sprite;
  private leftBookDropShadowWidth = -1;
  private rightBookDropShadowWidth = -1;
  private readonly staticLayer = new Container();
  private readonly staticLayerMask = new Graphics();
  private hasStaticLayerMask = false;
  private readonly leftThickness = new Sprite();
  private readonly rightThickness = new Sprite();
  private leftThicknessWidth = -1;
  private rightThicknessWidth = -1;
  private readonly bottomLayer = new Container();
  private readonly shadowLayer = new Container();
  private readonly flippingLayer = new Container();
  private readonly topShadowLayer = new Container();
  private readonly innerShadowMask = new Graphics();
  private readonly shadowMask = new Graphics();
  private readonly outerShadow: Sprite;
  private readonly innerShadow: Sprite;
  private readonly displayLayout: DisplayLayout;
  private currentPageIndex = 0;
  private activeCalculator: FlipCalculator | null = null;
  private activeLogicalDirection: FlipDirection | null = null;
  private needsRedraw = false;
  private flippingPage: PixiPage | null = null;
  private bottomPage: PixiPage | null = null;
  private activeHardCoverTransition: HardCoverTransition | null = null;
  private hardCoverFromPage: PixiPage | null = null;
  private hardCoverToPage: PixiPage | null = null;
  private hardCoverFromMesh: PerspectiveMesh | null = null;
  private hardCoverToMesh: PerspectiveMesh | null = null;
  private hardCoverFromTexture: RenderTexture | null = null;
  private hardCoverToTexture: RenderTexture | null = null;
  private temporaryFlippingPage: PixiPage | null = null;
  /** 临时翻起页的整页快照纹理，需随临时页一起 destroy(true)，否则每次翻页泄漏一张 RT。 */
  private temporaryFlippingSnapshot: Texture | null = null;
  private activeFlipPageIndexes: FlipPageIndexes | null = null;
  private foldCenteringEnabled = true;
  private animation: {
    from: Point;
    to: Point;
    elapsed: number;
    duration: number;
    turned: boolean;
    targetPageIndex?: number;
    source: FlipSource;
  } | null = null;
  private pendingAutoFlip: {
    direction: FlipDirection;
    corner: FlipCorner;
    count: number;
    source: FlipSource;
  } | null = null;
  private pendingDirectJump: {
    target: number;
    corner: FlipCorner;
    source: FlipSource;
  } | null = null;

  constructor(_options: PixiBookRendererOptions) {
    this.options = _options;
    this.displayLayout = createDisplayLayout(
      this.options.displayMode ?? 'double',
      this.options.pageWidth
    );
    this.currentPageIndex = this.displayLayout.initialPageIndex;
    this.root.sortableChildren = true;
    this.root.eventMode = 'static';
    this.centerContainer.sortableChildren = true;
    this.backgroundLayer.zIndex = 0;
    this.staticLayer.zIndex = 10;
    this.bottomLayer.zIndex = 20;
    this.shadowLayer.zIndex = 30;
    this.flippingLayer.zIndex = 40;
    this.topShadowLayer.zIndex = 50;
    this.outerShadow = createShadowSprite([
      [0, 'rgba(0,0,0,1)'],
      [0.6, 'rgba(0,0,0,0.1)'],
      [0.85, 'rgba(255, 255, 255, 0.1)'],
      [1, 'rgba(255, 255, 255, 0)']
    ]);
    this.innerShadow = createShadowSprite([
      [0, 'rgba(255,255,255,0.1)'],
      [0.7, 'rgba(0,0,0,0.7)'],
      [1, 'rgba(0,0,0,0)']
    ]);
    this.leftBookDropShadow = new Sprite();
    this.rightBookDropShadow = new Sprite();

    this.leftBookDropShadowContainer.addChild(this.leftBookDropShadow);
    this.rightBookDropShadowContainer.addChild(this.rightBookDropShadow);
    this.backgroundLayer.addChild(
      this.leftBookDropShadowContainer,
      this.rightBookDropShadowContainer,
      this.leftThickness,
      this.rightThickness
    );
    this.shadowLayer.addChild(this.outerShadow, this.shadowMask);
    this.shadowLayer.mask = this.shadowMask;
    this.topShadowLayer.addChild(this.innerShadow);
    this.topShadowLayer.mask = this.innerShadowMask;
    this.centerContainer.addChild(
      this.backgroundLayer,
      this.staticLayer,
      this.bottomLayer,
      this.shadowLayer,
      this.flippingLayer,
      this.topShadowLayer,
      this.innerShadowMask,
      this.staticLayerMask
    );
    this.root.addChild(this.centerContainer);

    this.options.app.ticker.add(this.tick);
    this.refresh();
    this.renderIdleSpread();
  }

  /** 销毁 Pixi 容器与 ticker 监听，释放 Renderer 持有的显示对象。 */
  destroy(): void {
    this.options.app.ticker.remove(this.tick);
    this.destroyTemporaryFlippingPage();
    this.detachHardCoverMeshes();
    this.root.destroy({ children: true });
    this.releaseHardCoverResources();
  }

  /** 销毁临时翻起页及其整页快照纹理（RT），避免单页模式每次翻页泄漏显存。 */
  private destroyTemporaryFlippingPage(): void {
    this.temporaryFlippingPage?.root.destroy({ children: true });
    this.temporaryFlippingPage = null;
    this.temporaryFlippingSnapshot?.destroy(true);
    this.temporaryFlippingSnapshot = null;
  }

  /** 用图片纹理替换指定页面内容，适合上层组件加载真实页面图。 */
  setPageImage(index: number, texture: Texture): void {
    const page = this.pages[index];
    if (!page) return;

    page.content.removeChildren();
    const sprite = new Sprite(texture);
    sprite.width = this.options.pageWidth;
    sprite.height = this.options.pageHeight;
    page.content.addChild(sprite);
  }

  /** 从 canvas 创建纹理并设置到指定页，canvas 销毁后纹理仍然有效。 */
  setPageImageFromCanvas(index: number, canvas: HTMLCanvasElement): void {
    const texture = Texture.from(canvas);
    this.setPageImage(index, texture);
  }

  /** 释放所有通过 `setPageImage` / `setPageImageFromCanvas` 设置的图片纹理。 */
  releasePageTextures(): void {
    for (const page of this.pages) {
      const child = page.content.children[0];
      if (child instanceof Sprite) {
        const tex = child.texture;
        child.removeFromParent();
        if (tex && tex !== Texture.WHITE && tex !== Texture.EMPTY) {
          tex.destroy(true);
        }
      }
    }
  }

  /** 标记需在下一帧重绘静态跨页，减少连续调用时的冗余渲染。 */
  markDirty(): void {
    this.needsRedraw = true;
  }

  /** 重新绘制阴影遮罩并标记重绘（不立即渲染，由下一帧 tick 执行）。 */
  refresh(): void {
    this.drawShadowMask();
    this.markDirty();
  }

  /** 返回是否有正在播放的翻页动画。 */
  isAnimating(): boolean {
    return this.animation !== null;
  }

  /** 返回当前静止状态下跨页左侧页面索引。 */
  getCurrentPageIndex(): number {
    return this.currentPageIndex;
  }

  /** 供 Book 在增删页后修正当前页码并标记重绘。 */
  setCurrentPageIndex(value: number): void {
    this.currentPageIndex = value;
    this.markDirty();
  }

  /** 根据翻页方向计算翻起页和底页的页面索引。 */
  getFlipPageIndexes(direction: FlipDirection): { flipping: number; bottom: number } {
    return this.displayLayout.flipPageIndexes(this.currentPageIndex, direction);
  }

  private getActiveFlipPageIndexes(direction: FlipDirection): FlipPageIndexes {
    return this.activeFlipPageIndexes ?? this.getFlipPageIndexes(direction);
  }

  /** 返回书本在内部坐标系中的矩形信息。 */
  getRect(): PageRect {
    return {
      left: 0,
      top: 0,
      width: this.displayLayout.bookWidth,
      height: this.options.pageHeight,
      pageWidth: this.options.pageWidth
    };
  }

  /** 将 Pixi 全局指针坐标转换为书本内部坐标（centerContainer 空间）。 */
  globalToBook(event: FederatedPointerEvent): Point {
    const local = this.centerContainer.toLocal(event.global);
    return { x: local.x, y: local.y };
  }

  /** 将浏览器 client 坐标转换为书本内部坐标（centerContainer 空间）。 */
  clientToBook(clientX: number, clientY: number): Point {
    // 使用实际挂载到 DOM 的 canvas，确保 CSS 缩放后的坐标仍然正确。
    const view =
      (this.options.app.renderer.events.domElement as HTMLCanvasElement | null) ??
      this.options.app.canvas;
    const screenPoint = clientPointToElement(
      view,
      clientX,
      clientY,
      this.options.app.screen.width,
      this.options.app.screen.height
    );
    const local = this.centerContainer.toLocal(screenPoint);
    return { x: local.x, y: local.y };
  }

  /** 将书本坐标转换成当前翻页计算器使用的页面坐标。 */
  bookToPage(point: Point, direction: FlipDirection): Point {
    if (direction === 'forward') {
      return {
        x: this.isSingleMode() ? point.x : point.x - this.options.pageWidth,
        y: point.y
      };
    }

    return { x: this.options.pageWidth - point.x, y: point.y };
  }

  /** 将翻页计算器的页面坐标转换回书本坐标。 */
  pageToBook(point: Point, direction: FlipDirection): Point {
    if (direction === 'forward') {
      return {
        x: this.isSingleMode() ? point.x : this.options.pageWidth + point.x,
        y: point.y
      };
    }

    return { x: this.options.pageWidth - point.x, y: point.y };
  }

  /** 判断指定书本坐标是否位于可拖拽翻页的角落区域（用于悬停预览、滚轮排除、点击翻页）。 */
  canStartAt(bookPoint: Point): boolean {
    return (
      this.isPointOnCorners(bookPoint) && this.checkDirection(this.getDirectionByPoint(bookPoint))
    );
  }

  /**
   * 判断指定书本坐标是否可作为侧边中间拖拽翻页的起点。
   * 页内非四角区域均可起手；方向是否可翻在起翻时再校验，
   * 以便首页右滑 / 末页左滑仍能触发边界提示。
   */
  canStartSideEdgeDragAt(bookPoint: Point): boolean {
    return this.isPointOnSideEdgeMiddle(bookPoint);
  }

  /**
   * 判断指定书本坐标是否可作为侧边/外侧空白拖拽翻页的起点。
   * 含：书页左右外缘中间带、书本左右外侧空白（画布内、书外）。
   */
  canStartBlankSideDragAt(bookPoint: Point): boolean {
    return this.canStartSideEdgeDragAt(bookPoint) || this.canStartOutsideBlankDragAt(bookPoint);
  }

  /**
   * 判断指定书本坐标是否可作为拖拽翻页的起点。
   * 侧边中间带、外侧空白与四角互斥分区。
   */
  canStartDragAt(bookPoint: Point): boolean {
    return this.canStartBlankSideDragAt(bookPoint);
  }

  /**
   * 判断坐标是否落在书本左右外侧空白（画布内、书页矩形外）。
   * 首页左侧 / 末页右侧空白仍允许起手，起翻失败时由边界提示承接。
   */
  canStartOutsideBlankDragAt(bookPoint: Point): boolean {
    if (!this.isPointInBookVerticalBand(bookPoint)) return false;

    return bookPoint.x <= 0 || bookPoint.x >= this.displayLayout.bookWidth;
  }

  /**
   * 中央拖拽起翻时，若按下点离外缘较远（页面中部起翻），返回折页应从其滑入的外缘 X（书本坐标）；
   * 若按下点已贴近外缘（原侧边带范围内），返回 null 表示无需滑入过渡、沿用瞬时起翻。
   * @param direction 指定翻页方向时按该方向外缘计算（单页手势起翻）；否则按按下点左右半区。
   */
  getMiddleDragIntroEdgeX(bookPoint: Point, direction?: FlipDirection): number | null {
    const operatingDistance = this.getOperatingDistance();
    const bookWidth = this.displayLayout.bookWidth;
    const edgeX = direction
      ? direction === 'forward'
        ? bookWidth
        : 0
      : bookPoint.x < bookWidth / 2
        ? 0
        : bookWidth;
    if (Math.abs(bookPoint.x - edgeX) <= operatingDistance) return null;
    return edgeX;
  }

  /**
   * hover 预览滑入的起点：靠近书角、四周内缩 INSET 的近角点（书本坐标）。
   * 内缩保证首帧即为有效小折角，避免折角退化（angle→0、裁剪多边形不足）导致的闪烁。
   */
  getCornerIntroStart(bookPoint: Point): Point {
    const inset = BookConstants.MIDDLE_DRAG_EDGE_INSET_PX;
    const bookWidth = this.displayLayout.bookWidth;
    const height = this.options.pageHeight;
    return {
      x: bookPoint.x < bookWidth / 2 ? inset : bookWidth - inset,
      y: bookPoint.y < height / 2 ? inset : height - inset
    };
  }

  /** 控制当前折页是否按翻页进度移动整本书；仅 hover 预览会临时关闭。 */
  setFoldCenteringEnabled(enabled: boolean): void {
    if (!this.activeCalculator) return;
    this.foldCenteringEnabled = enabled;
  }

  isHardCoverFoldActive(): boolean {
    return this.activeHardCoverTransition !== null;
  }

  /** 切换当前侧边拖动使用的物理纸角，不重新排列活动图层。 */
  setFoldCorner(corner: FlipCorner): void {
    this.activeCalculator?.setCorner(corner);
  }

  /**
   * 从指定书本坐标开始一次手动翻页，并准备底页、翻起页和计算器状态。
   * @param logicalDirection 显式翻页方向；未传时按落点左右半区推断（四角/双页）。
   * @param source 来源为 user 且方向不可翻时触发边界提示；程序调用默认不提示。
   */
  startFold(
    bookPoint: Point,
    pageIndexes?: { flipping: number; bottom: number },
    logicalDirection?: FlipDirection,
    source: FlipSource = 'programmatic'
  ): boolean {
    if (this.animation) return false;
    this.finishAnimation(false);
    this.resetActiveLayers(false);

    const resolvedDirection = logicalDirection ?? this.getDirectionByPoint(bookPoint);
    const direction = this.getCalculationDirection(resolvedDirection);
    const corner: FlipCorner = bookPoint.y >= this.options.pageHeight / 2 ? 'bottom' : 'top';

    // 步骤1：方向越界时仅对用户操作发出边界提示
    if (!this.checkDirection(resolvedDirection)) {
      if (source === 'user') this.emitBoundFlipAttempt(resolvedDirection);
      return false;
    }

    const { flipping, bottom } = pageIndexes ?? this.getFlipPageIndexes(resolvedDirection);
    this.activeFlipPageIndexes = { flipping, bottom };
    this.activeLogicalDirection = resolvedDirection;
    const sourceFlippingPage = this.pages[flipping] ?? null;
    // bottom 页可能缺失（封面/封底的空白侧），此时透明处理，不阻止翻页。
    const bottomPage = this.pages[bottom] ?? null;
    if (!sourceFlippingPage) return false;

    // 单页模式始终用临时快照伪造一张空白纸背作为翻起层，保持前进/后退一致的三层结构：
    // 静态层显示真实页正面、翻起层显示空白纸背、底层显示被覆盖或露出的另一页。
    // 前进翻页时正在显示的当前页本就在静态层；后退翻页是该前进翻页的逆向播放，
    // 需把真实的上一页放入静态层作为落下的正面，由真实页反映其加载状态，避免快照过早抓到空白内容。
    const useTemporaryFlap = this.isSingleMode();
    const flippingPage = useTemporaryFlap
      ? this.createTemporaryPage(sourceFlippingPage, 'back')
      : sourceFlippingPage;

    this.flippingPage = flippingPage;
    this.bottomPage = bottomPage;
    this.activeCalculator = new FlipCalculator(
      direction,
      corner,
      this.options.pageWidth,
      this.options.pageHeight
    );
    this.activeHardCoverTransition = resolveHardCoverTransition({
      coverFlipType: this.options.coverFlipType,
      displayMode: this.options.displayMode ?? 'double',
      currentPageIndex: this.currentPageIndex,
      pageCount: this.pages.length,
      direction: resolvedDirection,
      flippingPageIndex: flipping,
      bottomPageIndex: bottom
    });
    this.hardCoverFromPage = this.activeHardCoverTransition
      ? (this.pages[this.activeHardCoverTransition.fromPageIndex] ?? null)
      : null;
    this.hardCoverToPage = this.activeHardCoverTransition
      ? (this.pages[this.activeHardCoverTransition.toPageIndex] ?? null)
      : null;
    if (!this.hardCoverFromPage || !this.hardCoverToPage) {
      this.activeHardCoverTransition = null;
      this.hardCoverFromPage = null;
      this.hardCoverToPage = null;
    }

    if (!useTemporaryFlap) {
      flippingPage.root.parent?.removeChild(flippingPage.root);
    }
    if (bottomPage) {
      bottomPage.root.parent?.removeChild(bottomPage.root);
      this.bottomLayer.addChild(bottomPage.root);
    }
    if (this.isSingleReverseBackFlip(resolvedDirection)) {
      sourceFlippingPage.root.parent?.removeChild(sourceFlippingPage.root);
      sourceFlippingPage.resetTransform();
      this.staticLayer.addChild(sourceFlippingPage.root);
    }
    this.flippingLayer.addChildAt(flippingPage.root, 0);
    if (this.activeHardCoverTransition && this.hardCoverFromPage && this.hardCoverToPage) {
      this.hardCoverFromPage.root.parent?.removeChild(this.hardCoverFromPage.root);
      this.hardCoverToPage.root.parent?.removeChild(this.hardCoverToPage.root);
      this.hardCoverFromPage.resetTransform();
      this.hardCoverToPage.resetTransform();
      if (this.hardCoverFromPage === this.hardCoverToPage) {
        this.flippingLayer.addChild(this.hardCoverFromPage.root);
      } else {
        this.flippingLayer.addChild(this.hardCoverToPage.root, this.hardCoverFromPage.root);
      }
      this.setupHardCoverMeshes();
      flippingPage.root.visible =
        !useTemporaryFlap &&
        flippingPage !== this.hardCoverFromPage &&
        flippingPage !== this.hardCoverToPage;
    }
    this.updateThickness(direction);
    this.updateBookDropShadow(direction);

    return true;
  }

  /** 将当前手动翻页拖动到新的书本坐标。 */
  foldTo(bookPoint: Point, shadowProgress?: number): void {
    if (!this.activeCalculator) return;
    this.applyCalculation(
      this.bookToPage(bookPoint, this.activeCalculator.getDirection()),
      true,
      shadowProgress
    );
  }

  /** 真实人工拖拽开始；悬停折角预览不调用。 */
  beginManualFlip(): void {
    this.options.onManualFlipStart?.({ pageIndex: this.currentPageIndex });
  }

  /**
   * 以指定进度预览翻页效果。
   * @param target 翻页完成后显示的页面索引
   * @param progress 翻页进度，0 表示不翻，0.5 表示翻到一半，1 表示翻完
   * @param corner 翻页角，默认使用底角
   */
  flipToPage(target: number, progress: number, corner: FlipCorner = 'bottom'): boolean {
    if (target < 0 || target >= this.pages.length) return false;
    if (progress <= 0) return false;

    const targetSpread = this.normalizeTargetSpreadIndex(target);
    const direction: FlipDirection = targetSpread > this.currentPageIndex ? 'forward' : 'back';
    if (targetSpread === this.currentPageIndex) return true;
    if (!this.checkDirection(direction)) return false;

    const clamped = Math.min(1, progress);

    if (clamped >= 1) {
      this.finishAnimation(false);
      this.resetActiveLayers(false);
      this.currentPageIndex = targetSpread;
      this.renderIdleSpread();
      this.options.onPageChange?.({ pageIndex: targetSpread, pageCount: this.pages.length });
      return true;
    }

    const y = corner === 'bottom' ? this.options.pageHeight - 2 : 2;
    const bookX = direction === 'forward' ? this.displayLayout.bookWidth - 4 : 4;
    if (!this.startFold({ x: bookX, y }, this.getDirectJumpPageIndexes(targetSpread, direction)))
      return false;

    const pageX = this.isSingleReverseBackFlip(direction)
      ? this.options.pageWidth * (2 * clamped - 1)
      : this.options.pageWidth * (1 - 2 * clamped);
    const pageY = corner === 'bottom' ? this.options.pageHeight - 5 : 5;
    this.applyCalculation({ x: pageX, y: pageY });

    return true;
  }

  /**
   * 按明确的起止页渲染翻页进度，使时间轴正放和倒放得到同一组确定帧。
   */
  flipBetweenPages(
    from: number,
    target: number,
    progress: number,
    corner: FlipCorner = 'bottom'
  ): boolean {
    if (from < 0 || from >= this.pages.length) return false;
    if (target < 0 || target >= this.pages.length) return false;

    const fromSpread = this.normalizeTargetSpreadIndex(from);
    const targetSpread = this.normalizeTargetSpreadIndex(target);
    if (
      !shouldRenderPageTransitionFrame(this.currentPageIndex, fromSpread, targetSpread, progress)
    ) {
      return true;
    }

    const frame = resolvePageTransitionFrame(fromSpread, targetSpread, progress);

    if (frame.type === 'idle') {
      return this.setIdlePageIndex(frame.pageIndex);
    }

    if (this.currentPageIndex !== frame.fromPageIndex) {
      this.finishAnimation(false);
      this.resetActiveLayers();
      this.currentPageIndex = frame.fromPageIndex;
      this.renderIdleSpread();
    }

    const direction: FlipDirection = frame.direction;
    if (!this.checkDirection(direction)) return false;

    const y = corner === 'bottom' ? this.options.pageHeight - 2 : 2;
    const bookX = direction === 'forward' ? this.displayLayout.bookWidth - 4 : 4;
    if (
      !this.startFold(
        { x: bookX, y },
        this.getDirectJumpPageIndexes(frame.targetPageIndex, direction)
      )
    ) {
      return false;
    }

    const pageX = this.isSingleReverseBackFlip(direction)
      ? this.options.pageWidth * (2 * frame.progress - 1)
      : this.options.pageWidth * (1 - 2 * frame.progress);
    const pageY = corner === 'bottom' ? this.options.pageHeight - 5 : 5;
    this.applyCalculation({ x: pageX, y: pageY });

    return true;
  }

  /** 结束当前手动拖拽，并根据拖拽位置决定回弹或完成翻页。 */
  stopFold(source: FlipSource = 'programmatic'): void {
    if (!this.activeCalculator) return;

    const position = this.activeCalculator.getPosition();
    const corner = this.activeCalculator.getCorner();
    const y = corner === 'bottom' ? this.options.pageHeight : 0;
    const reverseBack = this.isActiveSingleReverseBackFlip();
    const shouldTurn = shouldCommitFold({
      positionX: position.x,
      positionY: position.y,
      pageWidth: this.options.pageWidth,
      pageHeight: this.options.pageHeight,
      displayMode: this.options.displayMode ?? 'double',
      reverseBack
    });
    const dest = shouldTurn
      ? { x: reverseBack ? this.options.pageWidth : -this.options.pageWidth, y }
      : { x: reverseBack ? -this.options.pageWidth : this.options.pageWidth, y };

    this.animateTo(position, dest, shouldTurn, false, undefined, source);
  }

  /** 自动向后翻一页。 */
  next(
    corner: FlipCorner = 'bottom',
    quick = false,
    source: FlipSource = 'programmatic'
  ): number | null {
    if (this.animation) return this.queueAutoFlip('forward', corner, source);

    // 步骤1：末页继续往后翻时，仅用户操作触发边界提示
    if (!this.checkDirection('forward')) {
      if (source === 'user') this.emitBoundFlipAttempt('forward');
      return null;
    }
    const y = corner === 'bottom' ? this.options.pageHeight - 2 : 2;
    const bookPoint = { x: this.displayLayout.bookWidth - 4, y };
    if (!this.startFold(bookPoint) || !this.activeCalculator) return null;

    const topMargin = this.options.pageHeight / 10;
    const yStart = corner === 'bottom' ? this.options.pageHeight - topMargin : topMargin;
    const yDest = corner === 'bottom' ? this.options.pageHeight : 0;
    const start = { x: this.options.pageWidth - topMargin, y: yStart };

    this.applyCalculation(start);
    return this.animateTo(
      start,
      { x: -this.options.pageWidth, y: yDest },
      true,
      quick,
      undefined,
      source
    );
  }

  /** 自动向前翻一页。 */
  prev(
    corner: FlipCorner = 'bottom',
    quick = false,
    source: FlipSource = 'programmatic'
  ): number | null {
    if (this.animation) return this.queueAutoFlip('back', corner, source);

    // 步骤1：首页继续往前翻时，仅用户操作触发边界提示
    if (!this.checkDirection('back')) {
      if (source === 'user') this.emitBoundFlipAttempt('back');
      return null;
    }
    const y = corner === 'bottom' ? this.options.pageHeight - 2 : 2;
    const bookPoint = { x: 4, y };
    if (!this.startFold(bookPoint) || !this.activeCalculator) return null;

    const topMargin = this.options.pageHeight / 10;
    const yStart = corner === 'bottom' ? this.options.pageHeight - topMargin : topMargin;
    const yDest = corner === 'bottom' ? this.options.pageHeight : 0;
    // 单页后退是前进翻页的逆向播放，需反转起止点；双页模式仍用原始后退几何，避免影响其翻页动画。
    const reverseBack = this.isSingleReverseBackFlip('back');
    const start = reverseBack
      ? { x: -this.options.pageWidth + topMargin, y: yStart }
      : { x: this.options.pageWidth - topMargin, y: yStart };
    const dest = reverseBack
      ? { x: this.options.pageWidth, y: yDest }
      : { x: -this.options.pageWidth, y: yDest };

    this.applyCalculation(start);
    return this.animateTo(start, dest, true, quick, undefined, source);
  }

  /** 跳到起始跨页。 */
  first(): boolean {
    return this.animateToPage(this.displayLayout.initialPageIndex);
  }

  /** 跳到末尾跨页。 */
  last(): boolean {
    const pageCount = this.pages.length;
    if (pageCount <= 0) return false;

    const target = this.isSingleMode()
      ? pageCount - 1
      : pageCount % 2 === 0
        ? pageCount - 1
        : Math.max(this.displayLayout.initialPageIndex, pageCount - 2);

    return this.animateToPage(target);
  }

  /** 每帧推进自动翻页动画，并在动画结束时提交页面索引。 */
  private readonly tick = (ticker: { deltaMS: number }): void => {
    if (!this.animation || !this.activeCalculator) {
      if (this.needsRedraw) {
        this.needsRedraw = false;
        this.renderIdleSpread();
      }
      return;
    }

    this.animation.elapsed += ticker.deltaMS;
    const progress = Math.min(1, this.animation.elapsed / this.animation.duration);

    if (progress >= 1) {
      // 动画最后一帧：不再调用 applyCalculation，避免 rest 位置产生退化裁剪多边形导致闪屏
      const turned = this.animation.turned;
      const source = this.animation.source;
      const direction = this.activeLogicalDirection ?? this.activeCalculator.getDirection();
      const targetPageIndex = this.animation.targetPageIndex;
      this.animation = null;

      if (turned) {
        this.currentPageIndex =
          targetPageIndex ??
          this.currentPageIndex +
            (direction === 'forward' ? this.displayLayout.pageStep : -this.displayLayout.pageStep);
        this.options.onPageChange?.({
          pageIndex: this.currentPageIndex,
          pageCount: this.pages.length
        });
      }

      this.resetActiveLayers();
      this.renderIdleSpread();
      if (!turned && source === 'user') {
        this.options.onManualFlipRestore?.({ pageIndex: this.currentPageIndex });
      }
      if (!this.playPendingDirectJump()) {
        this.playQueuedAutoFlip();
      }
      return;
    }

    // 还原动画接近终点时折痕角度趋近 0，几何计算不稳定，跳过后续帧避免闪烁。
    // 此时 eased 已接近 1，视觉差异可忽略。
    if (!this.animation.turned && progress >= 1) return;

    const point = {
      x: this.animation.from.x + (this.animation.to.x - this.animation.from.x) * progress,
      y: this.animation.from.y + (this.animation.to.y - this.animation.from.y) * progress
    };
    this.applyCalculation(point /* , progress < 0.8 */);
  };

  /** 根据页面序号和展示模式计算书脊阴影方向。 */
  private getPageSpineSide(index: number): PageSpineSide {
    if (this.isSingleMode()) {
      return index === 0 ? 'first' : 'right';
    }

    if (index === 0) return 'first';
    const mod = this.pages.length % 2 === 0;
    if (index === this.pages.length - 1 && mod) return 'last';
    return index % 2 === 0 ? 'right' : 'left';
  }

  /** 重建指定页面的书脊阴影，addPage/removePage/insertPage 后调用。 */
  rebuildSpineShadow(index: number): void {
    const page = this.pages[index];
    if (!page) return;
    page.setupSpineShadow(this.getPageSpineSide(index));
    page.setupRoundedCorners(resolvePageCornerSide(index, this.options.displayMode ?? 'double'));
  }

  /** 设置指定页面是否需要绘制书脊阴影，并立即重建该页阴影。 */
  setPageNeedSpineShadow(index: number, needSpineShadow: boolean): void {
    const page = this.pages[index];
    if (!page) return;
    page.needSpineShadow = needSpineShadow;
    this.rebuildSpineShadow(index);
  }

  /** 创建一段自动动画；完整翻页行程为 2×pageWidth，对应 BookConstants.FLIPPING_TIME。 */
  private animateTo(
    from: Point,
    to: Point,
    turned: boolean,
    quick = false,
    targetPageIndex?: number,
    source: FlipSource = 'programmatic'
  ): number {
    const distance = Math.max(Math.abs(from.x - to.x), Math.abs(from.y - to.y));
    // 折页动画在页面坐标下完整翻过一页约 2×pageWidth，与单/双页可见宽度无关。
    const fullFlipDistance = this.options.pageWidth * 2;
    const duration = calculateFlipAnimationDuration(
      distance,
      fullFlipDistance,
      BookConstants.FLIPPING_TIME,
      quick
    );

    this.animation = {
      from,
      to,
      elapsed: 0,
      duration,
      turned,
      targetPageIndex,
      source
    };

    // 翻过去（turned）即在动画起点通知一次手动翻页，回弹不通知；
    // 自动播放走 flipBetweenPages 不经过此处，故不会重复出声。
    if (turned) {
      const direction =
        this.activeLogicalDirection ?? this.activeCalculator?.getDirection() ?? 'forward';
      const toPageIndex =
        targetPageIndex ??
        this.currentPageIndex +
          (direction === 'forward' ? this.displayLayout.pageStep : -this.displayLayout.pageStep);
      this.options.onManualFlip?.({
        fromPageIndex: this.currentPageIndex,
        toPageIndex,
        source
      });
    }

    return duration;
  }

  private queueAutoFlip(direction: FlipDirection, corner: FlipCorner, source: FlipSource): number {
    if (this.pendingAutoFlip?.direction === direction) {
      this.pendingAutoFlip.count += 1;
      this.pendingAutoFlip.corner = corner;
      this.pendingAutoFlip.source = source;
    } else if (this.pendingAutoFlip) {
      this.pendingAutoFlip.count -= 1;
      if (this.pendingAutoFlip.count <= 0) {
        this.pendingAutoFlip = null;
      }
    } else {
      this.pendingAutoFlip = { direction, corner, count: 1, source };
    }

    this.fastForwardCurrentAnimation();
    return this.animation?.duration ?? 0;
  }

  private fastForwardCurrentAnimation(): void {
    if (!this.animation) return;
    this.animation.elapsed = Math.max(this.animation.elapsed, this.animation.duration * 0.78);
  }

  private playQueuedAutoFlip(): void {
    while (!this.animation && this.pendingAutoFlip) {
      const queued = this.pendingAutoFlip;
      queued.count -= 1;
      if (queued.count <= 0) {
        this.pendingAutoFlip = null;
      }

      const duration =
        queued.direction === 'forward'
          ? this.next(queued.corner, true, queued.source)
          : this.prev(queued.corner, true, queued.source);
      if (duration !== null) return;
    }
  }

  private animateToPage(
    target: number,
    corner: FlipCorner = 'bottom',
    quick = false,
    source: FlipSource = 'programmatic'
  ): boolean {
    if (target < this.displayLayout.initialPageIndex || target >= this.pages.length) return false;
    if (target === this.currentPageIndex && !this.animation) return true;

    this.pendingAutoFlip = null;
    if (this.animation) {
      this.pendingDirectJump = { target, corner, source };
      this.fastForwardCurrentAnimation();
      return true;
    }

    const direction: FlipDirection = target > this.currentPageIndex ? 'forward' : 'back';
    if (!this.checkDirection(direction)) return false;

    const y = corner === 'bottom' ? this.options.pageHeight - 2 : 2;
    const bookPoint = { x: direction === 'forward' ? this.displayLayout.bookWidth - 4 : 4, y };
    if (
      !this.startFold(bookPoint, this.getDirectJumpPageIndexes(target, direction)) ||
      !this.activeCalculator
    )
      return false;

    const topMargin = this.options.pageHeight / 10;
    const yStart = corner === 'bottom' ? this.options.pageHeight - topMargin : topMargin;
    const yDest = corner === 'bottom' ? this.options.pageHeight : 0;
    const start = this.isSingleReverseBackFlip(direction)
      ? { x: -this.options.pageWidth + topMargin, y: yStart }
      : { x: this.options.pageWidth - topMargin, y: yStart };
    const dest = this.isSingleReverseBackFlip(direction)
      ? { x: this.options.pageWidth, y: yDest }
      : { x: -this.options.pageWidth, y: yDest };

    this.applyCalculation(start);
    this.animateTo(start, dest, true, quick, target, source);
    return true;
  }

  private getDirectJumpPageIndexes(
    target: number,
    direction: FlipDirection
  ): { flipping: number; bottom: number } {
    if (this.isSingleMode()) {
      return direction === 'forward'
        ? { flipping: this.currentPageIndex, bottom: target }
        : { flipping: target, bottom: this.currentPageIndex };
    }

    return direction === 'forward'
      ? { flipping: target, bottom: target + 1 }
      : { flipping: target + 1, bottom: target };
  }

  private normalizeTargetSpreadIndex(target: number): number {
    if (this.isSingleMode()) return target;
    if (target <= 0) return this.displayLayout.initialPageIndex;
    if (target === this.pages.length - 1 && this.pages.length % 2 === 0) return target;
    return target % 2 === 0 ? target - 1 : target;
  }

  private playPendingDirectJump(): boolean {
    const jump = this.pendingDirectJump;
    if (!jump) return false;

    this.pendingDirectJump = null;
    return this.animateToPage(jump.target, jump.corner, true, jump.source);
  }

  private setIdlePageIndex(target: number): boolean {
    if (target < this.displayLayout.initialPageIndex || target >= this.pages.length) return false;

    this.pendingAutoFlip = null;
    this.finishAnimation(false);
    this.resetActiveLayers();
    const changed = target !== this.currentPageIndex;
    this.currentPageIndex = target;
    this.renderIdleSpread();
    if (changed) {
      this.options.onPageChange?.({ pageIndex: target, pageCount: this.pages.length });
    }
    return true;
  }

  /** 终止当前动画，并按需恢复到静止跨页状态。 */
  private finishAnimation(reset: boolean): void {
    this.animation = null;
    if (reset) {
      this.resetActiveLayers();
      this.renderIdleSpread();
    }
  }

  /** 执行一次翻页几何计算，并把结果同步到页面、阴影、厚度和居中位置。 */
  private applyCalculation(
    pagePoint: Point,
    updateShadow: boolean = true,
    shadowProgress?: number
  ): void {
    if (!this.activeCalculator || !this.flippingPage) return;
    const paperGeometryValid = this.activeCalculator.calc(pagePoint);
    const calculationPath = resolveFlipCalculationPath(
      Boolean(
        this.activeHardCoverTransition && this.hardCoverFromPage && this.hardCoverToPage
      ),
      paperGeometryValid
    );

    if (calculationPath === 'hard-cover') {
      this.applyHardCoverCalculation(pagePoint);
      return;
    }

    if (calculationPath === 'skip') {
      if (!this.shouldKeepStaticLayerMask(this.activeCalculator.getDirection())) {
        this.clearStaticLayerMask();
      }
      return;
    }

    const direction = this.activeCalculator.getDirection();

    // 翻页接近还原时折痕角度趋近 0 会产生退化裁剪多边形（不足 3 点），
    // 跳过本帧避免遮罩与 position/angle 不匹配导致闪烁。
    const flippingClipArea = this.activeCalculator.getFlippingClipArea();
    const bottomClipArea = this.activeCalculator.getBottomClipArea();
    if (flippingClipArea.length < 3 || bottomClipArea.length < 3) return;

    const activeCorner = this.activeCalculator.getActiveCorner();
    const activeCornerBookPosition = this.pageToBook(activeCorner, direction);

    if (this.bottomPage) {
      const bottomPosition = this.activeCalculator.getBottomPagePosition();
      const bottomBookPosition = this.pageToBook(bottomPosition, direction);

      this.bottomPage.root.position.set(bottomBookPosition.x, bottomBookPosition.y);
      this.bottomPage.setAngle(0);
      this.bottomPage.setArea(
        bottomClipArea.map((point) => {
          const bookPoint = this.pageToBook(point, direction);

          return {
            x: bookPoint.x - bottomBookPosition.x,
            y: bookPoint.y - bottomBookPosition.y
          };
        })
      );
    }

    this.flippingPage.root.position.set(activeCornerBookPosition.x, activeCornerBookPosition.y);
    this.flippingPage.setAngle(this.activeCalculator.getAngle());
    const flippingClipAreaBook = flippingClipArea.map((point) => this.pageToBook(point, direction));
    const flippingClipAreaLocal = flippingClipAreaBook.map((point) => ({
      x: point.x - activeCornerBookPosition.x,
      y: point.y - activeCornerBookPosition.y
    }));
    const foldCreaseSegment = this.activeCalculator.getShadowLineSegment().map((point) => {
      const bookPoint = this.pageToBook(point, direction);

      return {
        x: bookPoint.x - activeCornerBookPosition.x,
        y: bookPoint.y - activeCornerBookPosition.y
      };
    }) as Segment;

    this.flippingPage.setArea(
      flippingClipAreaLocal,
      true,
      this.activeCalculator.getCorner(),
      foldCreaseSegment
    );

    this.updateInnerShadowMask(flippingClipAreaBook);
    this.updateStaticLayerMask(direction);
    this.updateThickness(direction);
    this.updateBookDropShadow(direction);

    const progress = this.activeCalculator.getFlippingProgress();
    const shadowStartPoint = this.pageToBook(
      this.activeCalculator.getShadowStartPoint(),
      direction
    );
    if (updateShadow) {
      this.updateShadows({
        pos: shadowStartPoint,
        angle: this.activeCalculator.getShadowAngle(),
        progress: shadowProgress ?? progress,
        direction
      });
    } else {
      this.outerShadow.visible = false;
      this.innerShadow.visible = false;
    }

    if (this.foldCenteringEnabled) {
      this.updateCentering(progress, direction);
    } else {
      this.updateCentering();
    }
  }

  /** 将封面作为刚性平板绕书脊旋转。 */
  private applyHardCoverCalculation(pagePoint: Point): void {
    if (
      !this.activeCalculator ||
      !this.activeHardCoverTransition ||
      !this.hardCoverFromPage ||
      !this.hardCoverToPage ||
      !this.hardCoverFromMesh ||
      !this.hardCoverToMesh
    ) {
      return;
    }

    const progress = resolveHardCoverProgress(
      pagePoint.x,
      this.options.pageWidth,
      this.isActiveSingleReverseBackFlip()
    );
    const frame =
      this.activeHardCoverTransition.motion === 'turn'
        ? resolveHardCoverFrame(progress)
        : resolveSingleHardCoverFrame(progress, this.activeHardCoverTransition.motion);

    this.applyHardCoverPageProjection(
      this.hardCoverFromMesh,
      this.activeHardCoverTransition.fromSide,
      frame.scaleX,
      frame.perspective
    );
    this.applyHardCoverPageProjection(
      this.hardCoverToMesh,
      this.activeHardCoverTransition.toSide,
      frame.scaleX,
      frame.perspective
    );
    this.hardCoverFromMesh.visible = frame.face === 'from';
    this.hardCoverToMesh.visible = frame.face === 'to';

    const logicalDirection = this.activeLogicalDirection ?? this.activeCalculator.getDirection();
    if (
      this.bottomPage &&
      this.bottomPage !== this.hardCoverFromPage &&
      this.bottomPage !== this.hardCoverToPage
    ) {
      this.bottomPage.resetTransform();
      this.bottomPage.root.position.set(
        this.isSingleMode() || logicalDirection === 'back' ? 0 : this.options.pageWidth,
        0
      );
    }

    this.outerShadow.visible = false;
    this.innerShadow.visible = false;
    this.clearStaticLayerMask();
    this.updateThickness(logicalDirection);
    this.updateBookDropShadow(logicalDirection);
    if (this.foldCenteringEnabled) {
      this.updateCentering(progress * 100, logicalDirection);
    } else {
      this.updateCentering();
    }
  }

  /** 用水平透视投影模拟绕竖直书脊的 Y 轴旋转。 */
  private applyHardCoverPageProjection(
    mesh: PerspectiveMesh,
    side: 'left' | 'right',
    scaleX: number,
    perspective: number
  ): void {
    const hingeX = this.isSingleMode() ? 0 : this.options.pageWidth;
    const projectedWidth = this.options.pageWidth * scaleX;
    const overflow = this.options.pageHeight * 0.035 * perspective;
    const height = this.options.pageHeight;

    if (side === 'left') {
      mesh.setCorners(
        hingeX - projectedWidth,
        -overflow,
        hingeX,
        0,
        hingeX,
        height,
        hingeX - projectedWidth,
        height + overflow
      );
      return;
    }

    mesh.setCorners(
      hingeX,
      0,
      hingeX + projectedWidth,
      -overflow,
      hingeX + projectedWidth,
      height + overflow,
      hingeX,
      height
    );
  }

  /** Snapshot both rigid-cover faces into meshes so their outer edges can use true perspective. */
  private setupHardCoverMeshes(): void {
    if (!this.hardCoverFromPage || !this.hardCoverToPage) return;

    this.ensureHardCoverResources();
    if (
      !this.hardCoverFromTexture ||
      !this.hardCoverToTexture ||
      !this.hardCoverFromMesh ||
      !this.hardCoverToMesh
    ) {
      return;
    }

    this.options.app.renderer.render({
      container: this.hardCoverFromPage.root,
      target: this.hardCoverFromTexture,
      clear: true,
      clearColor: [0, 0, 0, 0]
    });
    this.options.app.renderer.render({
      container: this.hardCoverToPage.root,
      target: this.hardCoverToTexture,
      clear: true,
      clearColor: [0, 0, 0, 0]
    });
    this.hardCoverFromPage.root.visible = false;
    this.hardCoverToPage.root.visible = false;
    this.flippingLayer.addChild(this.hardCoverToMesh, this.hardCoverFromMesh);
  }

  /** Allocate one stable pair of GPU resources and reuse it for every cover transition. */
  private ensureHardCoverResources(): void {
    if (this.hardCoverFromTexture && this.hardCoverToTexture) return;

    const textureOptions = {
      width: this.options.pageWidth,
      height: this.options.pageHeight,
      resolution: this.options.app.renderer.resolution,
      antialias: this.options.app.renderer.view.antialias
    };
    this.hardCoverFromTexture = RenderTexture.create(textureOptions);
    this.hardCoverToTexture = RenderTexture.create(textureOptions);
    this.hardCoverFromMesh = new PerspectiveMesh({
      texture: this.hardCoverFromTexture,
      verticesX: 12,
      verticesY: 12
    });
    this.hardCoverToMesh = new PerspectiveMesh({
      texture: this.hardCoverToTexture,
      verticesX: 12,
      verticesY: 12
    });
  }

  private detachHardCoverMeshes(): void {
    this.hardCoverFromMesh?.removeFromParent();
    this.hardCoverToMesh?.removeFromParent();
  }

  private releaseHardCoverResources(): void {
    this.detachHardCoverMeshes();
    this.hardCoverFromMesh?.destroy();
    this.hardCoverToMesh?.destroy();
    this.hardCoverFromMesh = null;
    this.hardCoverToMesh = null;
    this.hardCoverFromTexture?.destroy(true);
    this.hardCoverToTexture?.destroy(true);
    this.hardCoverFromTexture = null;
    this.hardCoverToTexture = null;
  }

  /** 渲染无翻页动作时的静态跨页，并隐藏动态阴影。 */
  private renderIdleSpread(): void {
    this.needsRedraw = false;
    this.clearStaticLayerMask();
    this.resetContainers(this.staticLayer);
    this.updateThickness();
    this.updateBookDropShadow();

    for (const placement of this.displayLayout.staticPages(this.currentPageIndex)) {
      const page = this.pages[placement.index];
      if (!page) continue;

      page.resetTransform();
      page.root.position.set(placement.x, 0);
      this.staticLayer.addChild(page.root);
    }

    this.outerShadow.visible = false;
    this.innerShadow.visible = false;
    this.updateCentering();
  }

  /** 计算指定跨页相对书本中心的水平偏移量。 */
  private spreadCenterOffset(leftIndex: number): number {
    if (this.isSingleMode()) return 0;

    const pageCount = this.pages.length;
    const leftExists = leftIndex >= 0 && leftIndex < pageCount;
    const rightExists = leftIndex + 1 >= 0 && leftIndex + 1 < pageCount;

    if (leftExists && !rightExists) return this.options.pageWidth / 2;
    if (!leftExists && rightExists) return -this.options.pageWidth / 2;
    return 0;
  }

  /** 根据当前跨页和可选翻页进度更新 centerContainer.x 实现跨页居中偏移。 */
  private updateCentering(progress?: number, direction?: FlipDirection): void {
    let offset = this.spreadCenterOffset(this.currentPageIndex);

    if (progress !== undefined && direction) {
      const toLeft =
        this.animation?.targetPageIndex ??
        (direction === 'forward'
          ? this.currentPageIndex + this.displayLayout.pageStep
          : this.currentPageIndex - this.displayLayout.pageStep);
      const toOffset = this.spreadCenterOffset(toLeft);
      const t = Math.max(0, Math.min(1, progress / 100));
      offset = offset + (toOffset - offset) * t;
    }

    // centerContainer 位于 root 下，会随 root.scale 一起缩放，此处使用书本逻辑坐标即可
    this.centerContainer.x = offset;
    this.centerContainer.y = 0;
  }

  /** 清理正在翻页的临时状态，并按需重置页面变换。 */
  private resetActiveLayers(resetPages = true): void {
    this.hardCoverFromPage?.resetTransform();
    this.hardCoverToPage?.resetTransform();
    this.detachHardCoverMeshes();
    this.foldCenteringEnabled = true;
    this.activeCalculator = null;
    this.activeLogicalDirection = null;
    this.activeHardCoverTransition = null;
    this.hardCoverFromPage = null;
    this.hardCoverToPage = null;
    this.animation = null;
    this.flippingPage = null;
    this.bottomPage = null;
    this.activeFlipPageIndexes = null;
    this.destroyTemporaryFlippingPage();
    this.resetContainers(this.bottomLayer);
    this.resetContainers(this.flippingLayer);
    if (this.innerShadow.parent === this.topShadowLayer) {
      this.topShadowLayer.removeChild(this.innerShadow);
    }
    this.resetContainers(this.topShadowLayer);
    if (this.innerShadow.parent !== this.topShadowLayer) {
      this.topShadowLayer.addChild(this.innerShadow);
    }
    this.innerShadowMask.clear();
    this.clearStaticLayerMask();

    if (resetPages) {
      for (const page of this.pages) {
        page.resetTransform();
      }
    }
  }

  /** 移除容器内所有子节点，用于重建渲染层。 */
  private resetContainers(container: Container): void {
    container.removeChildren();
  }

  /** 根据书本坐标所在左右半区判断翻页方向。 */
  private getDirectionByPoint(bookPoint: Point): FlipDirection {
    return bookPoint.x < this.displayLayout.bookWidth / 2 ? 'back' : 'forward';
  }

  /** 单页上一页复用下一页的左书脊几何模型，再反向播放。 */
  private getCalculationDirection(logicalDirection: FlipDirection): FlipDirection {
    return this.isSingleReverseBackFlip(logicalDirection) ? 'forward' : logicalDirection;
  }

  private isSingleReverseBackFlip(logicalDirection: FlipDirection): boolean {
    return this.isSingleMode() && logicalDirection === 'back';
  }

  private isActiveSingleReverseBackFlip(): boolean {
    return (
      this.activeLogicalDirection === 'back' &&
      this.activeCalculator?.getDirection() === 'forward' &&
      this.isSingleMode()
    );
  }

  /** 判断当前页码是否允许按指定方向翻页。 */
  private checkDirection(direction: FlipDirection): boolean {
    return this.displayLayout.canFlip(this.currentPageIndex, this.pages.length, direction);
  }

  /** 供交互层查询当前是否允许向指定方向翻页。 */
  canFlip(direction: FlipDirection): boolean {
    return this.checkDirection(direction);
  }

  /** 通知外层：用户朝不可翻方向继续翻页。 */
  emitBoundFlipAttempt(direction: FlipDirection): void {
    this.options.onBoundFlipAttempt?.({ direction });
  }

  /** 判断坐标是否落在书本页面范围内（不含外部留白）。 */
  private isPointOnPage(bookPoint: Point): boolean {
    return (
      bookPoint.x > 0 &&
      bookPoint.y > 0 &&
      bookPoint.x < this.displayLayout.bookWidth &&
      bookPoint.y < this.options.pageHeight
    );
  }

  /** 判断坐标是否落在书本高度的垂直条带内（含书外左右空白）。 */
  private isPointInBookVerticalBand(bookPoint: Point): boolean {
    return bookPoint.y > 0 && bookPoint.y < this.options.pageHeight;
  }

  /** 返回四角/侧边可操作区的边距（书本坐标 px）。 */
  private getOperatingDistance(): number {
    return Math.max(Math.hypot(this.options.pageWidth, this.options.pageHeight) / 8, 100);
  }

  /**
   * 判断坐标是否落在页面中间拖拽区（整页内、排除四角）。
   * 用于「往内拖」的中间拖拽起翻：页面内任意非四角位置都可起翻，与四角拖拽分区。
   */
  private isPointOnSideEdgeMiddle(bookPoint: Point): boolean {
    if (!this.isPointOnPage(bookPoint)) return false;

    // 四角由 canStartAt/角拖拽处理，这里只排除四角，其余整页均可起翻。
    return !this.isPointOnCorners(bookPoint);
  }

  /** 判断坐标是否落在书本四角的可操作区域内。 */
  private isPointOnCorners(bookPoint: Point): boolean {
    const operatingDistance = this.getOperatingDistance();

    return (
      this.isPointOnPage(bookPoint) &&
      (bookPoint.x < operatingDistance ||
        bookPoint.x > this.displayLayout.bookWidth - operatingDistance) &&
      (bookPoint.y < operatingDistance || bookPoint.y > this.options.pageHeight - operatingDistance)
    );
  }

  /** 绘制外阴影使用的固定矩形遮罩。 */
  private drawShadowMask(): void {
    this.shadowMask.clear();
    this.shadowMask.rect(0, 0, this.displayLayout.bookWidth, this.options.pageHeight);
    this.shadowMask.fill({ color: 0xffffff });
  }

  /**
   * 创建类似 CSS drop-shadow 的书本外轮廓投影纹理，轮廓宽度包含页面厚度。
   * @param extraHeight 纵向额外扩展量，用于覆盖厚度在上下边缘的收缩区域
   */
  private createBookDropShadowTexture(width: number, height: number, _extraHeight = 0): Texture {
    const padding = BookConstants.BOOK_DROP_SHADOW_PADDING;
    const canvas = document.createElement('canvas');
    canvas.width = width + padding * 2;
    canvas.height = height + padding * 2;
    const context = canvas.getContext('2d');
    if (!context) return Texture.WHITE;

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = 'rgb(42, 34, 24)';

    const shadowLayers = [
      { blur: 10, offsetY: 2, alpha: 0.55 },
      { blur: 18, offsetY: 6, alpha: 0 }
    ];

    // 步骤1：按完整轮廓尺寸绘制阴影层
    for (const layer of shadowLayers) {
      context.save();
      context.globalAlpha = layer.alpha;
      context.filter = `blur(${layer.blur}px)`;
      context.fillRect(padding, padding + layer.offsetY, width, height);
      context.restore();
    }

    // 步骤2：用 destination-out 挖空中心区域（高度减去上下扩展量），保留外围投影
    // context.globalCompositeOperation = 'destination-out';
    // context.fillStyle = 'rgb(0, 0, 0)';
    // context.fillRect(padding, padding + extraHeight, width, height - extraHeight * 2);
    context.globalCompositeOperation = 'source-over';

    return Texture.from(canvas);
  }

  /** 按页面厚度重绘单侧书本外轮廓投影，并把 sprite 对齐到该侧真实外轮廓。 */
  private updateBookDropShadowSprite(
    sprite: Sprite,
    side: 'left' | 'right',
    thicknessWidth: number
  ): void {
    const roundedThickness = Math.max(0, Math.round(thicknessWidth));
    // 按厚度比例计算纵向扩展量，覆盖厚度线条在上下边缘的收缩区域
    const extraHeight = Math.round(roundedThickness * 0.6);
    const outlineWidth = this.options.pageWidth + roundedThickness;
    const lastWidth =
      side === 'left' ? this.leftBookDropShadowWidth : this.rightBookDropShadowWidth;

    if (outlineWidth !== lastWidth) {
      const previousTexture = sprite.texture;
      sprite.texture = this.createBookDropShadowTexture(
        outlineWidth,
        this.options.pageHeight,
        extraHeight
      );
      if (previousTexture !== Texture.EMPTY && previousTexture !== Texture.WHITE) {
        previousTexture.destroy(true);
      }
      if (side === 'left') this.leftBookDropShadowWidth = outlineWidth;
      else this.rightBookDropShadowWidth = outlineWidth;
    }

    const padding = BookConstants.BOOK_DROP_SHADOW_PADDING;
    const x = side === 'left' ? -roundedThickness - padding : this.options.pageWidth - padding;
    sprite.position.set(x, -padding);
  }

  /** 根据当前静态跨页、页面厚度和翻页状态显示、隐藏或重绘左右外轮廓投影。 */
  private updateBookDropShadow(direction?: FlipDirection): void {
    const mode = this.options.displayMode ?? 'double';
    const hiddenSide =
      direction === undefined
        ? null
        : getHiddenThicknessSide({
            mode,
            direction,
            bottomPageIndex: this.getActiveFlipPageIndexes(direction).bottom,
            pageCount: this.pages.length
          });
    const thickness = calculateBookThickness({
      mode,
      currentPageIndex: this.currentPageIndex,
      pageCount: this.pages.length,
      pageWidth: this.options.pageWidth
    });
    const leftThickness = hiddenSide === 'left' ? 0 : thickness.left;
    const rightThickness = hiddenSide === 'right' ? 0 : thickness.right;

    this.updateBookDropShadowSprite(this.leftBookDropShadow, 'left', leftThickness);
    this.updateBookDropShadowSprite(this.rightBookDropShadow, 'right', rightThickness);

    if (this.isSingleMode()) {
      this.leftBookDropShadowContainer.visible = false;
      this.rightBookDropShadowContainer.visible = true;
      this.rightBookDropShadow.position.set(
        -BookConstants.BOOK_DROP_SHADOW_PADDING,
        -BookConstants.BOOK_DROP_SHADOW_PADDING
      );
      return;
    }

    const leftIndex = this.currentPageIndex;
    const rightIndex = this.currentPageIndex + 1;
    let showLeft = leftIndex >= 0 && leftIndex < this.pages.length;
    let showRight = rightIndex >= 0 && rightIndex < this.pages.length;

    if (direction) {
      const { bottom } = this.getActiveFlipPageIndexes(direction);
      const closeSide = isCloseBookStaticMaskState({
        displayMode: this.options.displayMode ?? 'double',
        direction,
        bottomPageIndex: bottom,
        pageCount: this.pages.length
      });

      if (closeSide === 'front') showLeft = false;
      if (closeSide === 'back') showRight = false;
    }

    this.leftBookDropShadowContainer.visible = showLeft;
    this.rightBookDropShadowContainer.visible = showRight;
  }

  /** 判断当前展示模式是否为单页模式。 */
  private isSingleMode(): boolean {
    return this.options.displayMode === 'single';
  }

  /** 在单页模式下创建临时翻起页，避免直接移动静态页本体。 */
  private createTemporaryPage(source: PixiPage, side: 'front' | 'back'): PixiPage {
    this.destroyTemporaryFlippingPage();

    const temporaryPage = new PixiPage(
      source.index,
      this.options.pageWidth,
      this.options.pageHeight,
      this.options.pageRadius,
      this.options.onPageErrorEdit,
      this.options.pageErrorEditLabel,
      this.options.onPageErrorRetry
    );
    temporaryPage.needSpineShadow = source.needSpineShadow;
    const paperBack = new Graphics();
    paperBack.roundRect(0, 0, this.options.pageWidth, this.options.pageHeight, 4);
    paperBack.fill({ color: 0xffffff, alpha: 1 });

    const texture = this.options.app.renderer.generateTexture({
      target: source.content,
      frame: new Rectangle(0, 0, this.options.pageWidth, this.options.pageHeight)
    });
    this.temporaryFlippingSnapshot = texture;
    const snapshot = new Sprite(texture);
    if (side === 'back') {
      snapshot.alpha = 0.05;
      snapshot.scale.x = -1;
      snapshot.x = this.options.pageWidth;
    }
    temporaryPage.content.addChild(paperBack, snapshot);
    temporaryPage.setupSpineShadow(side === 'front' ? this.getPageSpineSide(source.index) : 'left');
    const sourceCornerSide = resolvePageCornerSide(
      source.index,
      this.options.displayMode ?? 'double'
    );
    temporaryPage.setupRoundedCorners(resolvePageFaceCornerSide(sourceCornerSide, side));
    this.temporaryFlippingPage = temporaryPage;

    return temporaryPage;
  }

  /** 用翻起页面的裁剪多边形更新内阴影遮罩。 */
  private updateInnerShadowMask(clipArea: Point[]): void {
    this.innerShadowMask.clear();
    if (clipArea.length < 3) {
      this.innerShadow.visible = false;
      return;
    }
    this.innerShadow.visible = true;
    this.innerShadowMask.moveTo(clipArea[0].x, clipArea[0].y);
    for (let i = 1; i < clipArea.length; i++) {
      this.innerShadowMask.lineTo(clipArea[i].x, clipArea[i].y);
    }
    this.innerShadowMask.closePath();
    this.innerShadowMask.fill({ color: 0xffffff });
  }

  /** 在封面或封底闭合状态附近更新静态层可见遮罩。 */
  private updateStaticLayerMask(direction: FlipDirection): void {
    if (!this.activeCalculator) return;

    const { bottom } = this.getActiveFlipPageIndexes(direction);
    const maskSide = isCloseBookStaticMaskState({
      displayMode: this.options.displayMode ?? 'double',
      direction,
      bottomPageIndex: bottom,
      pageCount: this.pages.length
    });
    if (!maskSide) {
      this.clearStaticLayerMask();
      return;
    }

    const tangentLine = this.activeCalculator
      .getShadowLineSegment()
      .map((point) => this.pageToBook(point, direction)) as [Point, Point];
    const visiblePolygon = buildStaticLayerVisiblePolygon(
      {
        left: 0,
        top: 0,
        width: this.displayLayout.bookWidth,
        height: this.options.pageHeight
      },
      tangentLine,
      maskSide
    );
    if (!visiblePolygon) {
      if (!this.shouldKeepStaticLayerMask(direction)) {
        this.clearStaticLayerMask();
      }
      return;
    }

    this.staticLayerMask.clear();
    this.staticLayerMask.moveTo(visiblePolygon[0].x, visiblePolygon[0].y);
    for (let i = 1; i < visiblePolygon.length; i++) {
      this.staticLayerMask.lineTo(visiblePolygon[i].x, visiblePolygon[i].y);
    }
    this.staticLayerMask.closePath();
    this.staticLayerMask.fill({ color: 0xffffff });
    this.staticLayer.mask = this.staticLayerMask;
    this.hasStaticLayerMask = true;
  }

  /** 根据当前页码和翻页方向更新书本厚度效果。 */
  private updateThickness(direction?: FlipDirection): void {
    const mode = this.options.displayMode ?? 'double';
    const hiddenSide =
      direction === undefined
        ? null
        : getHiddenThicknessSide({
            mode,
            direction,
            bottomPageIndex: this.getActiveFlipPageIndexes(direction).bottom,
            pageCount: this.pages.length
          });
    const thickness = calculateBookThickness({
      mode,
      currentPageIndex: this.currentPageIndex,
      pageCount: this.pages.length,
      pageWidth: this.options.pageWidth
    });

    this.updateThicknessSprite(
      this.leftThickness,
      'left',
      hiddenSide === 'left' ? 0 : thickness.left
    );
    this.updateThicknessSprite(
      this.rightThickness,
      'right',
      hiddenSide === 'right' ? 0 : thickness.right
    );
  }

  /** 更新单侧厚度精灵的可见性、纹理和位置。 */
  private updateThicknessSprite(sprite: Sprite, side: 'left' | 'right', width: number): void {
    // 步骤 1：像素宽 → 线数（每线固定 1px，不压缩）
    const lineCount = getThicknessLineCount(width);
    const thicknessPx = lineCount * BookConstants.THICKNESS_LINE_WIDTH;
    const lastLineCount = side === 'left' ? this.leftThicknessWidth : this.rightThicknessWidth;

    sprite.visible = lineCount > 0;
    if (lineCount <= 0) {
      return;
    }

    // 步骤 2：线数变化时重绘纹理
    if (lineCount !== lastLineCount) {
      sprite.texture = this.createThicknessTexture(lineCount, side);
      if (side === 'left') this.leftThicknessWidth = lineCount;
      else this.rightThicknessWidth = lineCount;
    }

    // 步骤 3：按厚度像素宽定位（左侧伸出书外）
    const { radiusExtent } = resolveThicknessCornerGeometry(
      this.options.pageRadius,
      this.options.pageWidth,
      this.options.pageHeight,
      lineCount
    );
    sprite.position.set(
      side === 'left' ? -thicknessPx : this.displayLayout.bookWidth - radiusExtent,
      0
    );
  }

  /** 生成由细线组成的书页厚度纹理：每条线固定 1px，间距 1px。 */
  private createThicknessTexture(lineCount: number, side: 'left' | 'right'): Texture {
    const lines = Math.max(0, lineCount);
    const lineWidth = BookConstants.THICKNESS_LINE_WIDTH;
    const { radius, radiusExtent, textureWidth } = resolveThicknessCornerGeometry(
      this.options.pageRadius,
      this.options.pageWidth,
      this.options.pageHeight,
      lines
    );
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, textureWidth);
    canvas.height = Math.max(1, this.options.pageHeight);
    const context = canvas.getContext('2d');
    if (!context) return Texture.WHITE;

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.lineWidth = lineWidth;
    const maxLineCount = Math.max(1, getMaxThicknessLineCount(this.options.pageWidth));
    for (let i = 0; i < lines; i += 1) {
      const { lineX, inset } = resolveThicknessLineGeometry({
        radius,
        radiusExtent,
        lineOffset: i,
        lineCount: lines,
        maxLineCount,
        pageHeight: this.options.pageHeight,
        side
      });
      context.strokeStyle = i % 2 === 0 ? 'rgba(150, 150, 150, 1)' : 'rgba(220, 220, 220, 1)';
      context.beginPath();
      if (radius <= 0) {
        context.moveTo(lineX, inset);
        context.lineTo(lineX, this.options.pageHeight - inset);
      } else {
        this.drawThicknessLinePath(context, lineX, radius, side);
      }
      context.stroke();
    }

    const texture = Texture.from(canvas);
    // 厚度为 1px 线稿，用 nearest 避免线性过滤糊成灰带
    texture.source.scaleMode = 'nearest';
    return texture;
  }

  /** 绘制单条厚度线：顶部贝塞尔圆角、中间直线、底部贝塞尔圆角。 */
  private drawThicknessLinePath(
    context: CanvasRenderingContext2D,
    lineX: number,
    radius: number,
    side: 'left' | 'right'
  ): void {
    const height = this.options.pageHeight;
    if (radius <= 0) {
      context.moveTo(lineX, 0);
      context.lineTo(lineX, height);
      return;
    }

    const kappa = 0.5522847498;

    if (side === 'left') {
      context.moveTo(lineX + radius, 0);
      context.bezierCurveTo(
        lineX + radius * (1 - kappa),
        0,
        lineX,
        radius * (1 - kappa),
        lineX,
        radius
      );
      context.lineTo(lineX, height - radius);
      context.bezierCurveTo(
        lineX,
        height - radius * (1 - kappa),
        lineX + radius * (1 - kappa),
        height,
        lineX + radius,
        height
      );
    } else {
      context.moveTo(lineX - radius, 0);
      context.bezierCurveTo(
        lineX - radius * (1 - kappa),
        0,
        lineX,
        radius * (1 - kappa),
        lineX,
        radius
      );
      context.lineTo(lineX, height - radius);
      context.bezierCurveTo(
        lineX,
        height - radius * (1 - kappa),
        lineX - radius * (1 - kappa),
        height,
        lineX - radius,
        height
      );
    }
  }

  /** 清除静态层遮罩，让静态页完整显示。 */
  private clearStaticLayerMask(): void {
    this.staticLayerMask.clear();
    this.staticLayer.mask = null;
    this.hasStaticLayerMask = false;
  }

  /** 判断当前翻页状态下是否需要保留上一帧静态层遮罩。 */
  private shouldKeepStaticLayerMask(direction: FlipDirection): boolean {
    const { bottom } = this.getActiveFlipPageIndexes(direction);

    return shouldRetainStaticLayerMask({
      displayMode: this.options.displayMode ?? 'double',
      direction,
      bottomPageIndex: bottom,
      pageCount: this.pages.length,
      hasExistingMask: this.hasStaticLayerMask
    });
  }

  /** 根据翻页切线、角度和进度更新内外阴影。 */
  private updateShadows(shadow: ShadowData): void {
    // 使用 power4.in 缓动：翻页前期透明度下降缓慢，接近结束时快速衰减
    const t = Math.min(1, shadow.progress / 100);
    const eased = gsap.parseEase('power2.in')(t);
    const opacity = Math.max(0, (1 - eased) * 0.34);
    const height = this.options.pageHeight * 2;
    const width = Math.max(
      8,
      (this.options.pageWidth * 0.75 * Math.min(100, shadow.progress)) / 100
    );
    const anchorY = 100 / height;

    this.outerShadow.visible = true;
    this.outerShadow.alpha = opacity;
    this.outerShadow.position.set(shadow.pos.x, shadow.pos.y);
    this.outerShadow.rotation = Math.PI + shadow.angle + Math.PI / 2;
    this.outerShadow.anchor.set(0, anchorY);
    const outerTexW = this.outerShadow.texture.width;
    const outerTexH = this.outerShadow.texture.height;
    this.outerShadow.scale.x = (shadow.direction === 'forward' ? 1 : -1) * (width / outerTexW);
    this.outerShadow.scale.y = height / outerTexH;

    this.innerShadow.visible = true;
    this.innerShadow.alpha = opacity * 0.75;
    this.innerShadow.position.set(shadow.pos.x, shadow.pos.y);
    this.innerShadow.rotation = this.outerShadow.rotation;
    this.innerShadow.anchor.set(1, anchorY);
    const innerTexW = this.innerShadow.texture.width;
    const innerTexH = this.innerShadow.texture.height;
    this.innerShadow.scale.x =
      (shadow.direction === 'forward' ? 1 : -1) * ((width * 0.75) / innerTexW);
    this.innerShadow.scale.y = height / innerTexH;
  }
}
